import React, { useEffect, useState, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Flex,
  Text,
  Box,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  VStack,
  Avatar,
  useToast,
} from '@chakra-ui/react';
import { HiOutlineCloudUpload } from 'react-icons/hi';
import { editOrganizationSchema } from '../../_helpers/validationSchema';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import { editOrganization, getOrgDetails, setOrgLogo, setBadgeIcon } from '../../Services/AccountService';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';

// Assuming you have this action creator (add it to your actions file if missing)
const updateSelectedOrg = (updatedFields) => ({
  type: 'UPDATE_SELECTED_ORG',   // ← change to match your reducer/action type
  payload: updatedFields,
});

const initialValues = {
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  state: '',
  country: '',
};

const OrgInfo = ({ orgIcon, pushIcon }) => {
  const [orgDetails, setOrgDetails] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBadge, setIsUploadingBadge] = useState(false);

  // Local fallback in case Redux update is delayed or not working
  const [localUrls, setLocalUrls] = useState({
    orgIcon: null,
    badgeIcon: null,
  });

  const toast = useToast();
  const dispatch = useDispatch();

  const logoInputRef = useRef(null);
  const badgeInputRef = useRef(null);

  const lastUploaded = useRef(new Map());

  const fetchOrgDetails = async () => {
    try {
      const res = await getOrgDetails();
      setOrgDetails(res.data || initialValues);
    } catch (err) {
      console.error('Failed to fetch org details:', err);
    }
  };

  useEffect(() => {
    fetchOrgDetails();
  }, []);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await editOrganization(values);
      toast({
        title: 'Success',
        description: 'Organization information updated.',
        status: 'success',
        position: 'top-right',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update organization info.',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (
    event,
    uploadFn,
    fieldName,
    successMessage,
    setUploading
  ) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    const uploadKey = `${fieldName}-${file.name}-${file.size}-${Date.now()}`;
    if (lastUploaded.current.has(uploadKey)) {
      console.log('Skipping duplicate upload (likely Strict Mode)');
      return;
    }
    lastUploaded.current.set(uploadKey, true);

    event.currentTarget.value = '';
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('media', file); // ← adjust if your API uses different key (file/icon/logo/etc)

      const response = await uploadFn(formData);

      const newUrl =
        response?.data?.media_url ||
        response?.data?.url ||
        response?.data?.path ||
        response?.data?.[fieldName] ||
        response?.data?.location;

      if (newUrl) {
        // 1. Update localStorage (your original pattern)
        const storage = JSON.parse(localStorage.getItem('selectedOrg') || '{}');
        storage[fieldName] = newUrl;
        localStorage.setItem('selectedOrg', JSON.stringify(storage));

        // 2. Update Redux (this makes Avatar re-render immediately)
        dispatch(updateSelectedOrg({
          [fieldName]: newUrl,
        }));

        // 3. Local state fallback (shows instantly even if Redux is slow)
        setLocalUrls((prev) => ({
          ...prev,
          [fieldName]: newUrl,
        }));

        toast({
          title: 'Success',
          description: successMessage,
          status: 'success',
          position: 'top-right',
        });
      } else {
        console.warn('No URL returned from server for', fieldName);
      }
    } catch (err) {
      console.error(`Failed to upload ${fieldName}:`, err);
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.message || err.message || 'Could not upload image.',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setUploading(false);
      setTimeout(() => lastUploaded.current.delete(uploadKey), 3000);
    }
  };

  const triggerFileInput = (ref) => {
    ref.current?.click();
  };

  // Use localUrls if set, otherwise fall back to redux props
  const displayedOrgIcon = localUrls.orgIcon || orgIcon;
  const displayedPushIcon = localUrls.badgeIcon || pushIcon;

  return (
    <Flex justify="space-between" gap={10} flexWrap="wrap">
      {/* Left – Company Information */}
      <Box flex={1} minW="400px">
        {!!orgDetails.companyName && (
          <Formik
            initialValues={orgDetails}
            validationSchema={editOrganizationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched }) => (
              <Form>
                <Text fontSize="xl" mb={6}>Company Information</Text>

                <Box
                  bg="white"
                  p={8}
                  borderRadius="md"
                  boxShadow="rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"
                  maxW="600px"
                >
                  <VStack align="stretch" spacing={5}>
                    {Object.keys(initialValues).map((key) => (
                      <Field name={key} key={key}>
                        {({ field }) => (
                          <FormControl isInvalid={errors[key] && touched[key]}>
                            <FormLabel>{key.replace(/([A-Z])/g, ' $1')}</FormLabel>
                            <Input {...field} placeholder={`Enter ${key}`} />
                            <FormErrorMessage>{errors[key]}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    ))}

                    <PrimaryButton
                      label="Save Changes"
                      type="submit"
                      isLoading={isSubmitting}
                      mt={4}
                    />
                  </VStack>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>

      {/* Right – Branding Assets */}
      <Box flex={1} minW="380px">
        <Text fontSize="xl" mb={6}>Branding Assets</Text>

        {/* Organization Logo */}
        <Box mb={10}>
          <Text fontWeight="medium" mb={2}>Organization Logo</Text>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Used in headers, emails etc. Recommended: PNG with transparency, min 480px height.
          </Text>

          <Flex align="center" gap={6}>
            <Avatar
              size="2xl"
              src={displayedOrgIcon}
              name="Organization Logo"
              bg="gray.100"
            />
            <Box>
              <input
                type="file"
                accept="image/png,image/jpeg"
                ref={logoInputRef}
                style={{ display: 'none' }}
                onChange={(e) =>
                  handleImageUpload(
                    e,
                    setOrgLogo,
                    'orgIcon',
                    'Organization logo updated successfully',
                    setIsUploadingLogo
                  )
                }
              />
              <PrimaryButton
                label="Upload Logo"
                rightIcon={<HiOutlineCloudUpload />}
                onClick={() => triggerFileInput(logoInputRef)}
                isLoading={isUploadingLogo}
              />
            </Box>
          </Flex>
        </Box>

        {/* Push Notification Icon */}
        <Box>
          <Text fontWeight="medium" mb={2}>Push Notification Icon</Text>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Used in mobile push notifications.<br />
            <strong>Recommended:</strong> 192×192 or 512×512 px, PNG, transparent background.
          </Text>

          <Flex align="center" gap={6}>
            <Avatar
              size="2xl"
              src={displayedPushIcon}
              name="Push Icon"
              bg="gray.100"
            />
            <Box>
              <input
                type="file"
                accept="image/png"
                ref={badgeInputRef}
                style={{ display: 'none' }}
                onChange={(e) =>
                  handleImageUpload(
                    e,
                    setBadgeIcon,
                    'badgeIcon',
                    'Push notification icon updated successfully',
                    setIsUploadingBadge
                  )
                }
              />
              <PrimaryButton
                label="Upload Push Icon"
                variant="outline"
                rightIcon={<HiOutlineCloudUpload />}
                onClick={() => triggerFileInput(badgeInputRef)}
                isLoading={isUploadingBadge}
              />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

const mapStateToProps = (state) => ({
  orgIcon: state.auth?.selectedOrg?.orgIcon || state.auth?.selectedOrg?.iconUrl,
  pushIcon: state.auth?.selectedOrg?.pushIcon || state.auth?.selectedOrg?.badgeIcon
,
});

export default connect(mapStateToProps)(OrgInfo);